/**
 * Date display helpers.
 *
 * The API stores calendar dates as 'YYYY-MM-DD'. `new Date('2026-09-07')` is
 * parsed as UTC midnight, which US browsers then display as September 6.
 * Parse date-only strings as local calendar dates so the day never shifts.
 */
export function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) {
    const [y, mo, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const d = new Date(y, mo - 1, day);
    // Reject dates the constructor silently normalised (e.g. 2026-02-30 -> Mar 2).
    return d.getFullYear() === y && d.getMonth() === mo - 1 && d.getDate() === day ? d : null;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDate(value: string | number | Date | null | undefined, fallback = '—'): string {
  const d = parseDate(value);
  return d ? d.toLocaleDateString() : fallback;
}

export function formatDateTime(value: string | number | Date | null | undefined, fallback = '—'): string {
  const d = parseDate(value);
  return d ? d.toLocaleString() : fallback;
}

/**
 * Today as a local calendar date, 'YYYY-MM-DD'.
 *
 * The API stores inspection dates date-only, and one walkthrough of one unit on
 * one day is one inspection record. Sending `new Date().toISOString()` broke
 * both halves of that: it stored a UTC timestamp (so an evening inspection in
 * the US filed itself under tomorrow) and it made every submission unique, so a
 * double-tapped Submit on a bad connection recorded two inspections.
 */
export function todayLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
